import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  ActivityIndicator,
  ScrollView,
  Image,
  TouchableOpacity,
  Modal,
  Share,
  Alert,
} from 'react-native';
import type { V2Balance } from '../../types/v2';
import TabBar from '../../components/TabBar';
import SimpleHeader from '../../components/SimpleHeader';
import { COLORS } from '../../theme/themeTokens';
import { v2GetCard, v2GetCardWallet } from '../../services/v2/api';
import { maskedCardNumber } from '../../utils/cardNumber';

// ──────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────

interface TransactionDetailScreenProps {
  balance: V2Balance;
  transaction: any;
  onBack: () => void;
  onNavigate: (screen: string) => void;
}

// ──────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────

const TransactionDetailScreen: React.FC<TransactionDetailScreenProps> = ({
  balance,
  transaction,
  onBack,
  onNavigate,
}) => {
  const [cardNumberValue, setCardNumberValue] = useState<string | null>(null);
  const [walletName, setWalletName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);

  const tx = transaction;

  /** Extract the raw numeric amount */
  const getRawAmount = (): number => {
    if (typeof tx.amount === 'object' && tx.amount !== null) {
      return Number(tx.amount.amount ?? 0);
    }
    return Number(tx.amount ?? 0);
  };

  const rawAmount = getRawAmount();
  const isDeposit = rawAmount < 0;

  /** Format number with space as thousands separator and dot as decimal separator. */
  const formatAmount = (value: number | string | null): string => {
    if (value == null) return '';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return String(value);
    const [intPart, decPart] = num.toFixed(2).split('.');
    const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${withSpaces}.${decPart}`;
  };

  const amountDisplay = (() => {
    if (typeof tx.amount === 'object' && tx.amount !== null) {
      return `${formatAmount((tx.amount.amount ?? 0) / 100)} ${tx.amount.currency ?? ''}`;
    }
    return `${formatAmount((tx.amount ?? 0) / 100)} ${tx.currency ?? ''}`;
  })();

  const amountOnly = (() => {
    if (typeof tx.amount === 'object' && tx.amount !== null) {
      return formatAmount((tx.amount.amount ?? 0) / 100);
    }
    return formatAmount((tx.amount ?? 0) / 100);
  })();

  const currencyOnly = (() => {
    if (typeof tx.amount === 'object' && tx.amount !== null) {
      return tx.amount.currency ?? '';
    }
    return tx.currency ?? '';
  })();

  const organization = tx.terminal?.name ?? 'Deposit';


  /** Format "YYYY-MM-DD HH:mm:ss" → "dd.MM.yyyy HH:mm:ss" */
  const formatDateTime = (raw: string): string => {
    if (!raw) return '';
    const [datePart, timePart] = raw.split(' ');
    const parts = (datePart ?? '').split('-');
    if (parts.length === 3) {
      return `${parts[2]}.${parts[1]}.${parts[0]}${timePart ? ' ' + timePart : ''}`;
    }
    return raw;
  };

  // Capitalize first letter helper (safely handles null/undefined)
  const capitalizeFirst = (s: string | null | undefined): string => {
    if (!s) return '—';
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  useEffect(() => {
    let mounted = true;
    const cardId = balance.wallet_address ?? '';
    if (!cardId) return;

    const fetchExtra = async () => {
      try {
        setLoading(true);
        if (isDeposit) {
          // The card's public, already masked number ("5303 26** **** 9929"):
          // the last four is all this screen shows. It used to ask the
          // sensitive endpoint for the full number just to cut it down.
          const res = await v2GetCard(cardId).catch(() => null);
          if (mounted) {
            setCardNumberValue(maskedCardNumber(res?.data?.number));
          }
        } else {
          // Fetch wallet name
          const res = await v2GetCardWallet(cardId).catch(() => null);
          if (mounted) {
            setWalletName(res?.data?.name ?? null);
          }
        }
      } catch (err) {
        console.warn('[TransactionDetailScreen] fetchExtra failed', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchExtra();
    return () => { mounted = false; };
  }, [balance.wallet_address]);

  /** Build plain-text receipt from transaction fields */
  const buildReceiptText = (): string => {
    const lines: string[] = [];
    lines.push('Transaction Details');
    lines.push('───────────────────');
    lines.push(`Organization: ${organization}`);
    lines.push(`Amount: ${rawAmount > 0 ? '+' : ''}${amountOnly} ${currencyOnly}`);
    lines.push(`Date: ${formatDateTime(tx.created_at ?? '')}`);
    if (isDeposit) {
      if (cardNumberValue) lines.push(`Card number: ${cardNumberValue}`);
      if (tx.terminal?.city) lines.push(`City: ${tx.terminal.city}`);
      if (tx.terminal?.country) lines.push(`Country: ${tx.terminal.country}`);
    } else {
      if (walletName) lines.push(`To wallet: ${walletName}`);
    }
  lines.push(`Status: ${capitalizeFirst(tx.status)}`);
    return lines.join('\n');
  };

  const handleShareOther = async () => {
    setShareModalVisible(false);
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const receiptText = buildReceiptText();
    
    try {
      await Share.share({
        message: receiptText,
        title: 'Transaction Receipt',
      });
    } catch (err: any) {
      console.warn('[TransactionDetailScreen] Share error:', err?.message);
    }
  };

  const handleSavePdf = () => {
    setShareModalVisible(false);
    Alert.alert('Save as PDF', 'PDF generation is not yet available. The receipt text has been prepared for sharing.', [
      { text: 'Share as text', onPress: handleShareOther },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor={COLORS.white} />

      <SimpleHeader
        title="Transaction details"
        onBack={onBack}
        backgroundVariant="white"
      />

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {loading ? (
          <ActivityIndicator size="large" color={COLORS.brandOrange} style={styles.loader} />
        ) : (
          <>
            {/* Hero section */}
            <View style={styles.heroSection}>
              <Image
                source={require('../../assets/illustr_card.png')}
                style={styles.heroImage}
                resizeMode="contain"
              />
              <Text style={styles.heroOrganization}>{organization}</Text>
              <Text style={[styles.heroAmount, rawAmount > 0 && styles.heroAmountPositive]}>
                {rawAmount > 0 ? '+' : ''}{amountOnly} <Text style={[styles.heroCurrency, rawAmount > 0 && styles.heroAmountPositive]}>{currencyOnly}</Text>
              </Text>
            </View>

            <View style={styles.card}>
            {isDeposit ? (
              <>
                <View style={styles.row}>
                  <Text style={styles.label}>Card number</Text>
                  <Text style={styles.value}>{cardNumberValue ?? '—'}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Date</Text>
                  <Text style={styles.value}>{formatDateTime(tx.created_at ?? '')}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Organization</Text>
                  <Text style={styles.value}>{tx.terminal?.name ?? '—'}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>City</Text>
                  <Text style={styles.value}>{tx.terminal?.city ?? '—'}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Country</Text>
                  <Text style={styles.value}>{tx.terminal?.country ?? '—'}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Status</Text>
                  <Text style={[styles.value, tx.status === 'successful' && styles.valueSuccess]}>{capitalizeFirst(tx.status)}</Text>
                </View>
              </>
            ) : (
              <>
                <View style={styles.row}>
                  <Text style={styles.label}>From current account</Text>
                  <Text style={styles.value}>USDT</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>To wallet</Text>
                  <Text style={styles.value}>{walletName ?? '—'}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Date</Text>
                  <Text style={styles.value}>{formatDateTime(tx.created_at ?? '')}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Status</Text>
                  <Text style={[styles.value, tx.status === 'successful' && styles.valueSuccess]}>{capitalizeFirst(tx.status)}</Text>
                </View>
              </>
            )}
          </View>
          </>
        )}

        {/* Save and send button — opens native share sheet directly */}
        <TouchableOpacity
          style={styles.saveBtn}
          activeOpacity={0.7}
          onPress={handleShareOther}
        >
          <Image source={require('../../assets/download.png')} style={styles.saveBtnIcon} />
          <Text style={styles.saveBtnText}>Save and send</Text>
        </TouchableOpacity>
      </ScrollView>

      {/* Share modal (half-screen) */}
      <Modal
        visible={shareModalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setShareModalVisible(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShareModalVisible(false)}
        >
          <View style={styles.modalSheet}>
            <View style={styles.modalHandle} />
            <Text style={styles.modalTitle}>Share</Text>

            <TouchableOpacity style={styles.modalOption} activeOpacity={0.7} onPress={handleShareOther}>
              <View style={styles.modalOptionIconWrap}>
                <Text style={styles.modalOptionIconText}>↗</Text>
              </View>
              <Text style={styles.modalOptionText}>Share</Text>
            </TouchableOpacity>


          </View>
        </TouchableOpacity>
      </Modal>

      <TabBar activeTab="dashboard" onNavigate={onNavigate} />
    </View>
  );
};

// ──────────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  content: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 120,
  },
  loader: {
    marginVertical: 40,
  },
  card: {
    width: '100%',
    backgroundColor: COLORS.pureLight,
    borderRadius: 8,
    padding: 16,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  label: {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '400',
    color: COLORS.blueSteel,
  },
  value: {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '400',
    color: COLORS.primaryNavy,
    flexShrink: 1,
    textAlign: 'right',
    marginLeft: 12,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: '#E5E7EB',
    marginVertical: 8,
  },
  heroSection: {
    alignItems: 'center',
    marginBottom: 24,
  },
  heroImage: {
    width: 180,
    height: 120,
    marginBottom: 16,
  },
  heroOrganization: {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '400',
    color: COLORS.blueSteel,
    marginBottom: 8,
    textAlign: 'center',
  },
  heroAmount: {
    fontSize: 32,
    lineHeight: 48,
    fontWeight: '600',
    color: COLORS.primaryNavy,
    textAlign: 'center',
  },
  heroAmountPositive: {
    color: COLORS.mint,
  },
  heroCurrency: {
    fontSize: 26,
    lineHeight: 40,
    fontWeight: '600',
    color: COLORS.blueSteel,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: COLORS.primaryNavy,
    marginBottom: 12,
    textAlign: 'left',
  },
  sectionTitleInCard: {
    fontSize: 18,
    fontWeight: '700',
    color: COLORS.primaryNavy,
    marginBottom: 12,
    textAlign: 'left',
  },
  valueSuccess: {
    color: COLORS.mint,
  },

  // ── Save and send button ────────────────────────────────
  saveBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    marginTop: 20,
  },
  saveBtnIcon: {
    width: 16,
    height: 16,
    marginRight: 8,
    tintColor: COLORS.steelNavy,
  },
  saveBtnText: {
    fontFamily: 'Inter',
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '500',
    color: COLORS.steelNavy,
  },

  // ── Share modal (half-screen bottom sheet) ──────────────
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  modalSheet: {
    backgroundColor: COLORS.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingHorizontal: 24,
    paddingTop: 12,
    paddingBottom: 40,
    minHeight: '50%',
  },
  modalHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#D1D5DB',
    alignSelf: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: COLORS.primaryNavy,
    marginBottom: 24,
    textAlign: 'center',
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#E5E7EB',
  },
  modalOptionIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: COLORS.steelNavy,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  modalOptionIconText: {
    fontSize: 16,
    fontWeight: '700',
    color: COLORS.white,
  },
  modalOptionText: {
    fontSize: 18,
    fontWeight: '500',
    color: COLORS.primaryNavy,
  },
  modalCancelBtn: {
    marginTop: 20,
    paddingVertical: 14,
    borderRadius: 8,
    backgroundColor: COLORS.pureLight,
    alignItems: 'center',
  },
  modalCancelText: {
    fontSize: 18,
    fontWeight: '600',
    color: COLORS.blueSteel,
  },
});

export default TransactionDetailScreen;
